// ============================================================================
// The command center's buttons (item #702, part of #700), drawn to the approved
// round-5 demo: the item card's Approve / Push back / Park / Unpark / Pin /
// Priority / Merge, and the Feedback row's Complete / Won't fix / Duplicate.
//
// Every press is ONE call: writeBridge for the board, the shared feedback save
// for a report. What a press writes is decided by the bridge, not here — this
// file only gathers Ben's words and shows the answer. A refusal shows the
// bridge's own sentence, never a blank.
//
// Only the key's owner acts (decision 11). Until whoami says yes, every button
// is greyed with the reason; the page still reads.
// ============================================================================
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { BoardItem, SentBack } from './derive';
import { tierOf, TIERS, isParked, isProject, runLine, SESSION_FIRST_AFTER } from './derive';
import { buildRoute } from './writeup';
import type { Read, WriteAction, WriteResult, Whoami } from './api';
import { recommendedLetter, PUSHBACK_REASONS } from '../../../supabase/functions/build-room-bridge/handler';

export type WriteFn = (body: { action: WriteAction; item: number } & Record<string, unknown>) => Promise<Read<WriteResult>>;

export function actReason(who: Read<Whoami> | null): string | null {
  if (who === null) return 'Checking whether you can act from here…';
  if (!who.ok) return who.message;
  return who.data.canAct ? null : (who.data.reason ?? 'You cannot act from here.');
}

const btn = 'rounded-md border px-2.5 py-1 text-[12px] disabled:cursor-not-allowed disabled:opacity-45';
const BTN = {
  primary: `${btn} border-tcs-accent bg-tcs-accent font-semibold text-tcs-bg`,
  warn: `${btn} border-st-warn-border text-st-warn-fg`,
  plain: `${btn} border-tcs-border text-tcs-text`,
  ok: `${btn} border-st-ok-border bg-st-ok-bg font-semibold text-st-ok-fg`,
};

/**
 * A pane opens under the buttons, which sit at the foot of a long write-up in a
 * scrolling panel. Without this it opened below the panel's visible edge, and
 * Approve looked dead (Ben, 8 Oct: "I clicked approve and nothing happened").
 */
export function showPane(el: Pick<HTMLElement, 'scrollIntoView'> | null) {
  el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

function Pane({ title, children }: { title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => showPane(ref.current), []);
  return (
    <div ref={ref} className="mt-2 rounded-lg border border-tcs-border bg-tcs-bg p-3 text-[13px] text-tcs-text">
      <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted">{title}</div>
      {children}
    </div>
  );
}

function Words({ id, value, onChange, placeholder }: { id: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="mt-1.5 w-full rounded-md border border-tcs-border bg-tcs-bg-muted px-2 py-1.5 text-[13px] text-tcs-text"
    />
  );
}

function Radio({ name, checked, onChange, disabled, children }: { name: string; checked: boolean; onChange: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <label className={`mb-1 flex items-start gap-2 ${disabled ? 'opacity-50' : ''}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} disabled={disabled} className="mt-1" />
      <span>{children}</span>
    </label>
  );
}

export function Done({ wrote, at, signedBy }: { wrote: string; at: string; signedBy: string }) {
  const t = new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return (
    <div role="status" className="mb-2 rounded-lg border border-st-ok-border bg-st-ok-bg px-3 py-2 text-[13px] text-tcs-text">
      {wrote}
      <small className="mt-0.5 block text-[11px] text-tcs-text-muted">
        Written to the board at {t}, signed by {signedBy}.
      </small>
    </div>
  );
}

function Refused({ message }: { message: string }) {
  return (
    <div role="alert" className="mt-2 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-[13px] text-tcs-text">
      {message}
    </div>
  );
}

/** The demo's words for an Approve with no write-up behind it. */
export const NOTHING_TO_APPROVE = 'Nothing to approve until it is written up';

/** The run line for fixing a sent-back item in a session (item #1013): the item's own line, plus where the fix goes. */
export function sessionLine(item: BoardItem): string {
  return `${runLine(item).text}\nThe checker sent this back: read its newest comment on the item (first line "## Verifier") and fix every Must-fix finding on the Builder's own open pull request for the item, not a second one.`;
}

function CopyButton({ text }: { text: string }) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <button
        type="button"
        className={BTN.primary}
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(text);
              setSaid('Copied.');
            } catch {
              setSaid('Select the text above and copy it — this browser would not copy it for you.');
            }
          })();
        }}
      >
        Copy run line
      </button>
      {said && <span className="text-[12px] text-st-ok-fg">{said}</span>}
    </div>
  );
}

/** Who builds it, picked to start with: what the write-up recommends, Cowork when it says nothing. */
export function firstRoute(writeUp: string | undefined): 'cowork' | 'builder' {
  return buildRoute(writeUp).route === 'Builder' ? 'builder' : 'cowork';
}

type PaneId = 'approve' | 'push' | 'park' | 'unpark' | 'merge' | 'session' | null;

/**
 * The buttons under an item. Render with key={item.number} so a different item
 * never inherits the last one's typed words (PLAYBOOK §15a: a draft must not
 * survive its own save, nor follow the reader to another record).
 */
export function ItemActions({
  item,
  who,
  login,
  onWrite,
  onDone,
  sentBack = null,
}: {
  item: BoardItem;
  who: Read<Whoami> | null;
  login?: string | null;
  onWrite: WriteFn;
  onDone: (r: WriteResult, title: string) => void;
  /** Why it is on the sent-back pile (item #1013), when it is: decides the two buttons. */
  sentBack?: SentBack | null;
}) {
  const [pane, setPane] = useState<PaneId>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<WriteResult | null>(null);
  const [choice, setChoice] = useState<'recommendation' | 'own'>(item.write_up ? 'recommendation' : 'own');
  const [words, setWords] = useState('');
  const [reason, setReason] = useState('check-again');
  const [builds, setBuilds] = useState(firstRoute(item.write_up?.body));

  const blocked = actReason(who);
  const stage = item.labels.find((l) => l.startsWith('stage:'));
  const parked = isParked(item.labels);
  const decision = stage === 'stage:needs-decision';
  const mergeable = stage === 'stage:ready-to-merge';
  const settled = stage === 'stage:ready-to-close';
  // Item #855: an approved item (not a project) is in Ready to run; a Cowork run marks it being built.
  const approved = stage === 'stage:approved' && !isProject(item) && !sentBack;
  // Item #1013: after two send-backs, a session is offered first.
  const sessionFirst = !!sentBack && sentBack.sendBacks >= SESSION_FIRST_AFTER;
  const pinned = login ? item.assignees.includes(login) : item.assignees.length > 0;
  const letter = item.write_up ? recommendedLetter(item.write_up.body) : null;
  const route = buildRoute(item.write_up?.body);
  const tier = tierOf(item);

  const open = (p: PaneId) => {
    setPane(pane === p ? null : p);
    setWords('');
    setError(null);
  };

  async function press(action: WriteAction, extra: Record<string, unknown> = {}) {
    setBusy(true);
    setError(null);
    const r = await onWrite({ action, item: item.number, ...extra });
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setDone(r.data);
    setPane(null);
    setWords('');
    onDone(r.data, item.title);
  }

  const off = !!blocked || busy;

  const prioritySelect = (
    <select
      aria-label="Priority"
      value={tier < TIERS.length ? TIERS[tier].label : ''}
      disabled={off}
      title={blocked ?? undefined}
      onChange={(e) => void press('priority', { to: e.target.value })}
      className="rounded-md border border-tcs-border bg-tcs-bg-muted px-2 py-1 text-[12px] text-tcs-text disabled:opacity-45"
    >
      {tier >= TIERS.length && <option value="">Priority: not set</option>}
      {TIERS.map((t) => (
        <option key={t.label} value={t.label}>
          Priority: {t.name}
        </option>
      ))}
    </select>
  );

  const pinButton = (
    <button type="button" className={BTN.plain} disabled={off} title={blocked ?? undefined} onClick={() => void press(pinned ? 'unpin' : 'pin')}>
      {pinned ? 'Unpin' : 'Pin'}
    </button>
  );

  return (
    <div className="mt-2">
      {done && <Done wrote={done.wrote} at={done.at} signedBy={done.signed_by} />}
      <div className="flex flex-wrap items-center gap-1.5">
        {parked ? (
          <button type="button" className={BTN.primary} disabled={off} title={blocked ?? undefined} onClick={() => open('unpark')}>
            Unpark
          </button>
        ) : sentBack ? (
          // Item #1013 (option A, 28 Oct), as drawn in its demo: one press sends it back to
          // the Builder; a card whose only finding is a check that did not run has no button;
          // after two send-backs "Fix it in a session" comes first.
          sentBack.checksOnly ? (
            <span className="text-[12px] text-tcs-text-muted">Nothing to press: the checker runs again when the check does.</span>
          ) : (
            (() => {
              const send = (
                <button
                  key="send"
                  type="button"
                  className={sessionFirst ? BTN.plain : BTN.primary}
                  disabled={off}
                  title={blocked ?? undefined}
                  onClick={() => void press('send-back')}
                >
                  {sentBack.kind === 'builder' ? 'Start the Builder again' : sessionFirst ? 'Send back once more' : 'Send back to the Builder'}
                </button>
              );
              const session = (
                <button key="session" type="button" className={sessionFirst ? BTN.primary : BTN.plain} disabled={!!blocked} title={blocked ?? undefined} onClick={() => open('session')}>
                  Fix it in a session
                </button>
              );
              return sessionFirst ? [session, send] : [send, session];
            })()
          )
        ) : approved ? (
          // Item #855 (decision 1 option 1): the run line's own instruction, as a press.
          <button type="button" className={BTN.primary} disabled={off} title={blocked ?? undefined} onClick={() => void press('mark-building')}>
            Mark being built
          </button>
        ) : settled ? (
          <>
            {/* Item #827, as drawn in the approved 9 Oct demo: nothing closes until Ben presses Close it. */}
            <button type="button" className={BTN.ok} disabled={off} title={blocked ?? undefined} onClick={() => void press('close-settled')}>
              Close it
            </button>
            <button type="button" className={BTN.plain} disabled={off} title={blocked ?? undefined} onClick={() => void press('keep-open')}>
              Keep it open
            </button>
          </>
        ) : mergeable ? (
          <button type="button" className={BTN.ok} disabled={off} title={blocked ?? undefined} onClick={() => open('merge')}>
            Merge
          </button>
        ) : decision ? (
          <>
            {/* As drawn in the approved demo: nothing to approve until it is written up. */}
            <button
              type="button"
              className={BTN.primary}
              disabled={off || !item.write_up}
              title={blocked ?? (item.write_up ? undefined : NOTHING_TO_APPROVE)}
              onClick={() => {
                open('approve');
                setChoice('recommendation');
              }}
            >
              Approve{letter ? ` ${letter}` : ''}
            </button>
            <button type="button" className={BTN.warn} disabled={off} title={blocked ?? undefined} onClick={() => open('push')}>
              Push back ▾
            </button>
          </>
        ) : null}
        {!parked && (
          <button type="button" className={BTN.plain} disabled={off} title={blocked ?? undefined} onClick={() => open('park')}>
            Park
          </button>
        )}
        {!parked && pinButton}
        {prioritySelect}
      </div>

      {blocked && <p className="mt-1.5 text-[11.5px] text-tcs-text-muted">{blocked}</p>}
      {busy && <p className="mt-1.5 text-[11.5px] text-tcs-text-muted">Writing to the board…</p>}
      {error && <Refused message={error} />}

      {pane === 'session' && (
        <Pane title="Fix it in a session">
          <p className="mb-1.5 text-[12px] text-tcs-text-muted">
            Paste this into Cowork. The session reads the checker's newest comment on the item and fixes it on the Builder's own pull request.
          </p>
          <pre className="whitespace-pre-wrap break-words rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2 font-sans text-[12.5px] text-tcs-text">
            {sessionLine(item)}
          </pre>
          <CopyButton text={sessionLine(item)} />
        </Pane>
      )}

      {pane === 'approve' && (
        <Pane title="Approve">
          <Radio name="ap" checked={choice === 'recommendation'} onChange={() => setChoice('recommendation')} disabled={!item.write_up}>
            The recommendation{letter ? <>: <b>{letter}</b></> : ''}
            {!item.write_up && ' — there is no write-up yet'}
          </Radio>
          <Radio name="ap" checked={choice === 'own'} onChange={() => setChoice('own')}>
            A different option, in my words:
          </Radio>
          <Words
            id="approve-words"
            value={words}
            onChange={setWords}
            placeholder={choice === 'own' ? 'The option you mean (required)' : 'Optional: anything the build should know'}
          />
          <div className="mb-1 mt-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted">Who builds it</div>
          <Radio name="rt" checked={builds === 'cowork'} onChange={() => setBuilds('cowork')}>
            <b>I'll run it in Cowork</b>: a prompt is written and you run it.
          </Radio>
          <Radio name="rt" checked={builds === 'builder'} onChange={() => setBuilds('builder')}>
            <b>The Builder builds it</b>: it starts as soon as you approve, and its pull request comes to you to merge.
          </Radio>
          <p className="mt-1 text-[12px] text-tcs-text-muted">
            {route.route
              ? `The write-up recommends ${route.route}${route.reason ? `: ${route.reason}` : '.'}`
              : 'This write-up predates the build route, so Approve starts on Cowork.'}
            {route.parts.length > 0 && ` Parts of the app: ${route.parts.join(', ')}.`}
          </p>
          <div className="mt-2 flex gap-1.5">
            <button
              type="button"
              className={BTN.primary}
              disabled={off || (choice === 'own' && !words.trim())}
              onClick={() => void press('approve', { choice, words: words.trim() || undefined, route: builds })}
            >
              Approve
            </button>
            <button type="button" className={BTN.plain} onClick={() => setPane(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}

      {pane === 'push' && (
        <Pane title="Push back · why">
          {Object.entries(PUSHBACK_REASONS).map(([k, label]) => (
            <Radio key={k} name="pb" checked={reason === k} onChange={() => setReason(k)}>
              {label}
            </Radio>
          ))}
          <Words id="push-words" value={words} onChange={setWords} placeholder="Your words, which the Assessor reads (optional, but it helps)" />
          <div className="mt-2 flex gap-1.5">
            <button type="button" className={BTN.warn} disabled={off} onClick={() => void press('pushback', { reason, words: words.trim() || undefined })}>
              Send back
            </button>
            <button type="button" className={BTN.plain} onClick={() => setPane(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}

      {pane === 'park' && (
        <Pane title="Park">
          <p className="mb-1">It moves to the bottom of Needs you, with its priority showing. Nothing works on it until you unpark it.</p>
          <Words id="park-words" value={words} onChange={setWords} placeholder="Why, or until when (optional)" />
          <div className="mt-2 flex gap-1.5">
            <button type="button" className={BTN.plain} disabled={off} onClick={() => void press('park', { words: words.trim() || undefined })}>
              Park it
            </button>
            <button type="button" className={BTN.plain} onClick={() => setPane(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}

      {pane === 'unpark' && (
        <Pane title={`Unpark · ${item.write_up ? 'back into Needs you, where it was' : 'sends it to recon to be written up'}`}>
          <Words
            id="unpark-words"
            value={words}
            onChange={setWords}
            placeholder={item.write_up ? 'Optional: anything that changed' : 'Optional: what the recon should look at'}
          />
          <div className="mt-2 flex gap-1.5">
            <button type="button" className={BTN.primary} disabled={off} onClick={() => void press('unpark', { words: words.trim() || undefined })}>
              Unpark
            </button>
            <button type="button" className={BTN.plain} onClick={() => setPane(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}

      {pane === 'merge' && (
        <Pane title="Merge">
          <p>
            Posts your "/merge". The merge gate then checks three things: it's you, the item is ready to merge, and every check is green.
            If any fails, nothing merges and the gate says why on the item.
          </p>
          <div className="mt-2 flex gap-1.5">
            <button type="button" className={BTN.ok} disabled={off} onClick={() => void press('merge')}>
              Merge
            </button>
            <button type="button" className={BTN.plain} onClick={() => setPane(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}
    </div>
  );
}

// ----- Feedback -------------------------------------------------------------------

export type FeedbackKind = 'complete' | 'wont' | 'dup';

/**
 * Complete / Won't fix / Duplicate of on one report. `draft` pre-fills Complete
 * when the report's item finished another way (decision 7). `openItems` are the
 * board items still open for this report — "Close the board item too" closes them.
 */
export function FeedbackActions({
  feedbackId,
  reporter,
  draft,
  openItems,
  who,
  onSave,
}: {
  feedbackId: string;
  reporter: string;
  draft: string | null;
  openItems: BoardItem[];
  who: Read<Whoami> | null;
  onSave: (kind: FeedbackKind, text: string, closeItems: number[]) => Promise<string | null>;
}) {
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [text, setText] = useState('');
  const [closeToo, setCloseToo] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = actReason(who);
  const first = reporter.split(/\s+/)[0] || 'the reporter';

  const open = (k: FeedbackKind) => {
    setKind(kind === k ? null : k);
    setText(k === 'complete' && draft ? draft : '');
    setError(null);
  };

  async function go() {
    const t = text.trim();
    if (!t || !kind) return;
    if (kind === 'dup') {
      const id = t.toUpperCase();
      if (!/^AFB-\d{4}$/.test(id)) {
        setError(`"${t}" isn't a feedback ID. They look like AFB-0081.`);
        return;
      }
      if (id === feedbackId) {
        setError('A report cannot be a duplicate of itself.');
        return;
      }
    }
    setBusy(true);
    setError(null);
    const err = await onSave(kind, kind === 'dup' ? t.toUpperCase() : t, kind !== 'dup' && closeToo ? openItems.map((i) => i.number) : []);
    setBusy(false);
    if (err) setError(err);
    else {
      setKind(null);
      setText('');
    }
  }

  const off = !!blocked || busy;
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        <button type="button" className={BTN.primary} disabled={off} title={blocked ?? undefined} onClick={() => open('complete')}>
          Complete…
        </button>
        <button type="button" className={BTN.plain} disabled={off} title={blocked ?? undefined} onClick={() => open('wont')}>
          Won't fix…
        </button>
        <button type="button" className={BTN.plain} disabled={off} title={blocked ?? undefined} onClick={() => open('dup')}>
          Duplicate of…
        </button>
      </div>
      {blocked && <p className="mt-1.5 text-[11.5px] text-tcs-text-muted">{blocked}</p>}
      {kind && (
        <Pane title={kind === 'complete' ? `Resolution, sent to ${first}` : kind === 'wont' ? `Why it won't change, sent to ${first}` : 'Duplicate of which report?'}>
          <Words
            id="feedback-words"
            value={text}
            onChange={setText}
            placeholder={kind === 'dup' ? 'The other report, e.g. AFB-0081' : 'Written for the person who reported it (required)'}
          />
          {kind !== 'dup' && openItems.length > 0 && (
            <label className="mt-1.5 flex items-start gap-2">
              <input type="checkbox" checked={closeToo} onChange={(e) => setCloseToo(e.target.checked)} className="mt-1" />
              <span>
                Close the board item too ({openItems.map((i) => `“${i.title}”`).join(', ')}), with the same words as its closing comment
              </span>
            </label>
          )}
          {error && <Refused message={error} />}
          <div className="mt-2 flex gap-1.5">
            <button type="button" className={BTN.primary} disabled={off || !text.trim()} onClick={() => void go()}>
              {kind === 'complete' ? 'Complete' : kind === 'wont' ? 'Close without a change' : 'Mark duplicate'}
            </button>
            <button type="button" className={BTN.plain} onClick={() => setKind(null)}>
              Cancel
            </button>
          </div>
        </Pane>
      )}
    </div>
  );
}
